// src/context/ItineraryContext.tsx
import React, { createContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { ItinerarioDia, Lugar, ModoTransporte, obtenerPuntosTuristicos } from '../api/api';

/* --------------------------------------------------------------- */
/* 1) TIPOS AUXILIARES                                             */
/* --------------------------------------------------------------- */

/* ➊ Tu estructura ligera con solo coordenadas  (la dejamos) */
export interface DiaPoints {
  start?: { latitud: number; longitud: number };
  end?:   { latitud: number; longitud: number };
}

/* ➋ NUEVO — diccionarios completos para que MapView no rompa      */
/*     (si prefieres prescindir de ellos después, solo elimina      */
/*     la parte de MapView que los usa y estos ➋ ya no harían falta) */
export type DayStarts = Record<number, Lugar | undefined>;
export type DayEnds   = Record<number, Lugar | undefined>;

type PersistedItineraryState = {
  itinerarios?: ItinerarioDia[];
  perfil?: string;
  dias?: number;
  modoTransporte?: ModoTransporte;
  timingVersion?: string | null;
  startingPoint?: Lugar | null;
  dayPoints?: Record<number, DiaPoints>;
  dayStarts?: DayStarts;
  dayEnds?: DayEnds;
  fechaInicio?: string | null;
  fechaFin?: string | null;
};

const ITINERARY_STORAGE_KEY = 'ribeira_itinerary_state_v1';
export const CURRENT_TIMING_VERSION = 'travel-time-offset-v2';
const DEFAULT_PERSISTED_STATE: Required<PersistedItineraryState> = {
  itinerarios: [],
  perfil: '',
  dias: 1,
  modoTransporte: 'coche',
  timingVersion: null,
  startingPoint: null,
  dayPoints: {},
  dayStarts: {},
  dayEnds: {},
  fechaInicio: null,
  fechaFin: null,
};

const loadPersistedItineraryState = (): Required<PersistedItineraryState> => {
  if (typeof window === 'undefined') return DEFAULT_PERSISTED_STATE;

  try {
    const raw = window.localStorage.getItem(ITINERARY_STORAGE_KEY);
    if (!raw) return DEFAULT_PERSISTED_STATE;

    const parsed = JSON.parse(raw) as PersistedItineraryState;
    return {
      ...DEFAULT_PERSISTED_STATE,
      ...parsed,
      itinerarios: Array.isArray(parsed.itinerarios) ? parsed.itinerarios : [],
      dias: typeof parsed.dias === 'number' && parsed.dias > 0 ? parsed.dias : 1,
      modoTransporte:
        parsed.modoTransporte === 'andando' ||
        parsed.modoTransporte === 'bici' ||
        parsed.modoTransporte === 'coche'
          ? parsed.modoTransporte
          : 'coche',
    };
  } catch (error) {
    console.warn('[ItineraryContext] No se pudo restaurar el itinerario guardado', error);
    return DEFAULT_PERSISTED_STATE;
  }
};

/* --------------------------------------------------------------- */
/* 2) TIPO DEL CONTEXTO                                            */
/* --------------------------------------------------------------- */
interface ItineraryContextType {
  itinerarios        : ItinerarioDia[];
  setItinerarios     : React.Dispatch<React.SetStateAction<ItinerarioDia[]>>;

  puntosTuristicos   : Lugar[];
  setPuntosTuristicos: React.Dispatch<React.SetStateAction<Lugar[]>>;

  perfil             : string;
  setPerfil          : React.Dispatch<React.SetStateAction<string>>;

  dias               : number;
  setDias            : React.Dispatch<React.SetStateAction<number>>;

  modoTransporte     : ModoTransporte;
  setModoTransporte  : React.Dispatch<React.SetStateAction<ModoTransporte>>;

  timingVersion      : string | null;
  setTimingVersion   : React.Dispatch<React.SetStateAction<string | null>>;

  fechaInicio        : string | null;
  setFechaInicio     : React.Dispatch<React.SetStateAction<string | null>>;
  fechaFin           : string | null;
  setFechaFin        : React.Dispatch<React.SetStateAction<string | null>>;

  startingPoint      : Lugar | null;
  setStartingPoint   : React.Dispatch<React.SetStateAction<Lugar | null>>;

  /* —— tu objeto con solo coords ——————————————— */
  dayPoints          : Record<number, DiaPoints>;
  setDayPoints       : React.Dispatch<React.SetStateAction<Record<number, DiaPoints>>>;

  /* —— NUEVO: objetos completos (usados por MapView) —— */
  dayStarts          : DayStarts;
  setDayStarts       : React.Dispatch<React.SetStateAction<DayStarts>>;
  dayEnds            : DayEnds;
  setDayEnds         : React.Dispatch<React.SetStateAction<DayEnds>>;

  resetItinerary     : () => void;
}

export const ItineraryContext = createContext<ItineraryContextType | undefined>(undefined);

interface Props { children: ReactNode }

/* --------------------------------------------------------------- */
/* 3) PROVIDER                                                     */
/* --------------------------------------------------------------- */
export const ItineraryProvider: React.FC<Props> = ({ children }) => {
  const initialState = loadPersistedItineraryState();

  /* existentes -------------------------------------------------- */
  const [itinerarios,      setItinerarios]   = useState<ItinerarioDia[]>(initialState.itinerarios);
  const [puntosTuristicos, setPuntosTuristicos] = useState<Lugar[]>([]);
  const [perfil,           setPerfil]        = useState(initialState.perfil);
  const [dias,             setDias]          = useState(initialState.dias);
  const [modoTransporte,   setModoTransporte]= useState<ModoTransporte>(initialState.modoTransporte);
  const [timingVersion,    setTimingVersion] = useState<string | null>(initialState.timingVersion);
  const [fechaInicio,      setFechaInicio]   = useState<string | null>(initialState.fechaInicio);
  const [fechaFin,         setFechaFin]      = useState<string | null>(initialState.fechaFin);
  const [startingPoint,    setStartingPoint] = useState<Lugar | null>(initialState.startingPoint);

  /* tu estado ligero -------------------------------------------- */
  const [dayPoints, setDayPoints] = useState<Record<number, DiaPoints>>(initialState.dayPoints);

/* NUEVOS estados — usados tal cual en MapView ------------------ */
const [dayStarts, setDayStarts] = useState<DayStarts>(initialState.dayStarts);
const [dayEnds  , setDayEnds  ] = useState<DayEnds>(initialState.dayEnds);

  const resetItinerary = useCallback(() => {
    console.log('[ItineraryContext] resetItinerary');
    window.localStorage.removeItem(ITINERARY_STORAGE_KEY);
    setItinerarios([]);
    setPerfil('');
    setDias(1);
    setModoTransporte('coche');
    setTimingVersion(null);
    setFechaInicio(null);
    setFechaFin(null);
    setStartingPoint(null);
    setDayPoints({});
    setDayStarts({});
    setDayEnds({});
  }, []);

  useEffect(() => {
    const stateToPersist: PersistedItineraryState = {
      itinerarios,
      perfil,
      dias,
      modoTransporte,
      timingVersion,
      startingPoint,
      dayPoints,
      dayStarts,
      dayEnds,
      fechaInicio,
      fechaFin,
    };

    try {
      window.localStorage.setItem(ITINERARY_STORAGE_KEY, JSON.stringify(stateToPersist));
    } catch (error) {
      console.warn('[ItineraryContext] No se pudo guardar el itinerario', error);
    }
  }, [
    itinerarios,
    perfil,
    dias,
    modoTransporte,
    timingVersion,
    startingPoint,
    dayPoints,
    dayStarts,
    dayEnds,
    fechaInicio,
    fechaFin,
  ]);

  /* -------------------------------------------------------------- */
  /* cargar POIs externos una única vez                             */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try { setPuntosTuristicos(await obtenerPuntosTuristicos()); }
      catch (err) { console.error('Error al obtener puntos turísticos', err); }
    })();
  }, []);

  /* -------------------------------------------------------------- */
  /* expose everything                                              */
  /* -------------------------------------------------------------- */
  return (
    <ItineraryContext.Provider value={{
      itinerarios, setItinerarios,
      puntosTuristicos, setPuntosTuristicos,
      perfil, setPerfil,
      dias, setDias,
      modoTransporte, setModoTransporte,
      timingVersion, setTimingVersion,
      fechaInicio, setFechaInicio,
      fechaFin, setFechaFin,
      startingPoint, setStartingPoint,

      /* coords-only version */
      dayPoints, setDayPoints,

      /* Lugar-completos — MapView los necesita */
      dayStarts, setDayStarts,
  dayEnds  , setDayEnds,
      resetItinerary,
    }}>
      {children}
    </ItineraryContext.Provider>
  );
};
